/**
 * MEROXIO x Shopify Horizon — Quick Add (Variants) + Cart Drawer
 * ✅ Only clicked variant shows loader/tick
 * ✅ Other variants DO NOT hide (only disabled + low opacity)
 * ✅ Variants stay OPEN on click (mobile + desktop) via .is-open
 * ✅ Tick shows on success, stays 1s, then drawer opens
 * ✅ Header cart bubble updates instantly (.cart-bubble__text-count)
 */

async function horizonAddToCart(payload) {
  const body = payload?.items
    ? { items: payload.items }
    : { items: [{ id: Number(payload.id), quantity: Number(payload.quantity || 1) }] };

  const res = await fetch("/cart/add.js", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const errText = await res.text().catch(() => "");
    throw new Error(`ATC failed (${res.status}). ${errText}`);
  }

  return fetch("/cart.js", { headers: { Accept: "application/json" } }).then((r) => r.json());
}

async function getCart() {
  return fetch("/cart.js", { headers: { Accept: "application/json" } }).then((r) => r.json());
}

function dispatchHorizonCartUpdate(cart) {
  try {
    document.dispatchEvent(
      new CustomEvent("cart:update", {
        bubbles: true,
        detail: { data: { itemCount: cart?.item_count ?? 0, source: "product-form-component" } },
      })
    );
  } catch (_) {}
}

/**
 * ✅ FIX: Update header cart bubble immediately
 * Your theme class: .cart-bubble__text-count
 */
function updateHeaderCartBubble(cart) {
  const count = Number(cart?.item_count ?? 0);

  const el = document.querySelector(".cart-bubble__text-count");
  if (!el) return;

  el.textContent = String(count);

  const wrap = el.closest(".cart-bubble, .cart-count-bubble, .cart-icon-bubble");
  if (wrap) wrap.style.display = count > 0 ? "" : "none";
}

function openHorizonDrawer(cart) {
  dispatchHorizonCartUpdate(cart);
  updateHeaderCartBubble(cart);

  try { document.querySelector("cart-drawer-component")?.open?.(); } catch (_) {}
  try {
    const drawer = document.querySelector("cart-drawer");
    if (drawer) drawer.open = true;
  } catch (_) {}
}

/**
 * Increase quantity by line item key (drawer case)
 */
async function increaseLineItemByKey(lineKey, delta = 1) {
  const cart = await getCart();
  const line = cart.items.find((i) => i.key === lineKey);

  if (!line) throw new Error("Line item not found for key: " + lineKey);

  const nextQty = Number(line.quantity || 0) + Number(delta);

  const res = await fetch("/cart/change.js", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ id: lineKey, quantity: nextQty }),
  });

  if (!res.ok) {
    const errText = await res.text().catch(() => "");
    throw new Error(`Cart change failed (${res.status}). ${errText}`);
  }

  return getCart();
}

/* UI state */
function setVariantState(el, state) {
  el.classList.remove("is-loading", "is-added");
  if (state === "loading") el.classList.add("is-loading");
  if (state === "added") el.classList.add("is-added");
}

/**
 * Keep variants visible after click (mobile hover issue fix)
 */
function setQuickAddOpen(fromEl, open = true) {
  const btn = fromEl?.closest?.(".m_quick_add_button");
  if (!btn) return;
  btn.classList.toggle("is-open", open);
}

/**
 * Disable/enable other variants during request (NO HIDE)
 */
function lockSiblingVariants(clicked, lock = true) {
  const wrap = clicked.closest(".m_quick_add_variants");
  if (!wrap) return;

  wrap.querySelectorAll(".m_quickATCVariant").forEach((el) => {
    if (lock) {
      el.dataset._pe = el.style.pointerEvents || "";
      el.style.pointerEvents = "none";
      el.style.opacity = el === clicked ? "1" : "0.55";
    } else {
      el.style.pointerEvents = el.dataset._pe || "";
      el.style.opacity = "";
      delete el.dataset._pe;
    }
  });
}

document.addEventListener("click", async (e) => {
  const variantEl = e.target.closest(".m_quickATCVariant");
  const btnEl = e.target.closest(".m_quick_add_button");

  // 1) Variant pill click
  if (variantEl) {
    e.preventDefault();

    if (variantEl.classList.contains("m_quickATCVariantSoldOut")) return;
    if (variantEl.classList.contains("is-loading")) return;

    const lineKey = variantEl.getAttribute("data-key"); // drawer line item key (optional)
    const variantId = variantEl.getAttribute("data-id"); // product cards variant id (optional)
    if (!lineKey && !variantId) return;

    // keep variants visible on click
    setQuickAddOpen(variantEl, true);

    // loading state on clicked variant only
    setVariantState(variantEl, "loading");
    lockSiblingVariants(variantEl, true);

    try {
      let cart;

      if (lineKey) {
        cart = await increaseLineItemByKey(lineKey, 1);
      } else {
        cart = await horizonAddToCart({ id: variantId, quantity: 1 });
      }

      // ✅ Update header instantly
      dispatchHorizonCartUpdate(cart);
      updateHeaderCartBubble(cart);

      // ✅ SUCCESS: show tick immediately
      setVariantState(variantEl, "added");

      // ✅ Keep tick visible for 1s, THEN open drawer
      setTimeout(() => openHorizonDrawer(cart), 1000);

      // ✅ After drawer open, reset variant state back
      setTimeout(() => setVariantState(variantEl, "idle"), 1400);
    } catch (err) {
      console.error("Quick ATC error:", err);
      setVariantState(variantEl, "idle");
    } finally {
      lockSiblingVariants(variantEl, false);

      // ✅ Close variants AFTER drawer open + small buffer
      setTimeout(() => setQuickAddOpen(variantEl, false), 1500);
    }

    return;
  }

  // 2) If product has only 1 variant & button has data-variant-id
  if (btnEl && btnEl.hasAttribute("data-variant-id")) {
    e.preventDefault();

    const id = btnEl.getAttribute("data-variant-id");
    if (!id) return;

    btnEl.classList.add("is-loading");

    try {
      const cart = await horizonAddToCart({ id, quantity: 1 });

      // ✅ Update header instantly
      dispatchHorizonCartUpdate(cart);
      updateHeaderCartBubble(cart);

      // ✅ (Optional) Add tick UI to main button if you want (currently only drawer opens)
      // Keeping theme behaviour same:
      setTimeout(() => openHorizonDrawer(cart), 1000);
    } catch (err) {
      console.error("Quick Add button ATC error:", err);
    } finally {
      btnEl.classList.remove("is-loading");
    }
  }
});
